/**
 * @file: MarkdownRenderer.tsx
 *
 * An AI reply (`markdown` payload) — rendered via AIContentRenderer (markdown
 * + embedded widgets) on a bare surface, width-bounded so charts/tables don't
 * blow the bubble out to full width. We MEASURE the surface's actual width
 * (the 80% bubble cap can make it narrower than the estimate) and feed the
 * real inner width to the chart. `quoted` renders a one-line text preview.
 */

import {
  AIContentRenderer,
  AIWidgetType,
} from '@euler/app/components/ai/AIContentRenderer';
import { ScrollableTableContext } from '@euler/app/components/message/markdown/MarkdownTable';
import { Typography } from '@euler/app/components/typography';
import { markdownToPlain } from '@euler/app/flows/chat/utils/markdownToPlain';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { ChatMessagePayloadMarkdown } from '@euler/model/chat';
import { FC, memo, useState } from 'react';
import { View, useWindowDimensions } from 'react-native';
import { PayloadRendererProps } from './types';

/** `suggested_questions` is suppressed for a human/AI-composed message, but
 * KEPT for a bot reply (a real assistant turn) — tapping a chip re-asks the
 * bot. Other widgets (cards/charts) always render. */
const NON_BOT_DISABLED_AI_WIDGETS: AIWidgetType[] = ['suggested_questions'];

export const MarkdownRenderer: FC<
  PayloadRendererProps<ChatMessagePayloadMarkdown> & {
    isBot?: boolean;
    onSuggestionPress?: (question: string) => void;
  }
> = memo(({ payload, quoted, isBot, onSuggestionPress }) => {
  const { rpx } = useResponsiveContext();
  const { width: winW } = useWindowDimensions();
  const isMobile = useIsMobileLayout();
  // Bound the AI bubble so charts/tables don't force it full-width. Desktop's
  // conversation pane is wide, so the cap is more generous there than mobile's
  // 80%-of-screen.
  const aiMaxWidth = isMobile ? winW * 0.8 : rpx(560);
  const [aiMeasuredWidth, setAiMeasuredWidth] = useState<number | undefined>(
    undefined,
  );

  if (quoted) {
    // Flatten markdown to plain text — a one-line quote shouldn't show raw
    // `**bold**` / table pipes.
    return (
      <Typography.Text size={rpx(13)} color="#475569" numberOfLines={1}>
        {markdownToPlain(payload.markdown)}
      </Typography.Text>
    );
  }

  return (
    <View
      onLayout={e => setAiMeasuredWidth(e.nativeEvent.layout.width)}
      style={{
        backgroundColor: '#fff',
        maxWidth: aiMaxWidth,
        borderRadius: rpx(10),
        paddingHorizontal: rpx(12),
        paddingVertical: rpx(6),
        shadowColor: '#000',
        shadowOffset: { width: 0, height: rpx(1) },
        shadowOpacity: 0.08,
        shadowRadius: rpx(4),
        elevation: 1,
      }}
    >
      {/* A wide table keeps wrapping here instead of scrolling sideways:
          the conversation's inverted FlashList blanks every iOS cell after a
          nested horizontal scroller. */}
      <ScrollableTableContext.Provider value={false}>
        <AIContentRenderer
          contentWidth={(aiMeasuredWidth ?? aiMaxWidth) - rpx(24)}
          content={payload.markdown}
          disabledWidgets={isBot ? [] : NON_BOT_DISABLED_AI_WIDGETS}
          onSuggestionPress={onSuggestionPress}
        />
      </ScrollableTableContext.Provider>
    </View>
  );
});
